import { createRoot } from "react-dom/client";
import ReactModal from "react-modal";
import { BrowserRouter, Link as RouterLink } from "react-router-dom";
import {
  DefaultToastContainer,
  ToastProvider,
} from "react-toast-notifications";
import { theme } from "@ovou-inc/ovou-ui";
import { OvouLinkProvider } from "@ovou-inc/ovou-ui/design-system/Link";

// prettier-ignore
import "react-phone-input-2/lib/material.css";
// prettier-ignore
import "./main.css";
// prettier-ignore
import "./App.css";

import { API_MOCKING } from "constants";
import { AnalyticsProvider } from "context/Analytics";
import { ParagonIntegrationServiceProvider } from "features/integrations/context/ParagonIntegrationServiceProvider";
import { FeatureFlagProvider } from "providers/FeatureFlagProvider";
import App from "./App";
import { PermissionsProvider, UserProvider } from "./context";
import * as serviceWorker from "./serviceWorker";

// Some libraries (drag-and-drop + react-select, e.g. the Contact Info form)
// trigger the benign "ResizeObserver loop completed with undelivered
// notifications" browser notice — a resize callback that re-lays-out within the
// same frame. It breaks nothing, but webpack-dev-server's overlay surfaces it as
// an uncaught runtime error (and it can't be swallowed by a window 'error'
// listener). Debounce ResizeObserver callbacks to the next animation frame so the
// relayout never happens synchronously in the same frame — this removes the loop
// at its source, in dev and prod, instead of just hiding the overlay.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const container = document.getElementById("root");
const root = createRoot(container); // createRoot(container!) if you use TypeScript
// require mocks only if enabled for development
if (API_MOCKING === "enabled") {
  import("./mocks/browser").then(({ worker }) => worker.start());
}
// Allows ReactModal to handle hiding any content not related to the opened modal. This is only for accessibility purposes.
ReactModal.setAppElement("#root");

// react-toast-notifications hardcodes zIndex 1000 on its container, which
// sits below Chakra Modal (1400). Lift it above the modal layer via the
// shared theme token so toasts fired from inside a modal are visible.
const RaisedToastContainer = props => (
  <DefaultToastContainer
    {...props}
    style={{ ...(props.style || {}), zIndex: theme.zIndex.tooltip }}
  />
);

/*
 * react-router v5's Link takes `to`; the design system's `Link` takes `href`,
 * like an anchor. This adapter is the whole difference — it is defined once,
 * outside render, so it is not a new component type on every re-render.
 */
const RouterLinkAdapter = ({ href, ...rest }) => (
  <RouterLink to={href} {...rest} />
);

root.render(
  <BrowserRouter>
    {/*
     * Registers the router for the design system's `Link`, once, for the whole
     * app. Without it `Link` renders a plain anchor: the URL is right, but it
     * triggers a full page reload — the bundle re-parses, React boots from
     * scratch and app state is lost, with nothing in the console. It is a
     * provider rather than a prop precisely because that failure is silent.
     *
     * Inert until something renders a `Link`; it only supplies context. It sits
     * inside BrowserRouter because the adapter needs the router context.
     */}
    <OvouLinkProvider component={RouterLinkAdapter}>
      <AnalyticsProvider>
        <UserProvider>
          <PermissionsProvider>
            <ToastProvider
              autoDismiss
              components={{ ToastContainer: RaisedToastContainer }}
            >
              <FeatureFlagProvider>
                <ParagonIntegrationServiceProvider>
                  <App />
                </ParagonIntegrationServiceProvider>
              </FeatureFlagProvider>
            </ToastProvider>
          </PermissionsProvider>
        </UserProvider>
      </AnalyticsProvider>
    </OvouLinkProvider>
  </BrowserRouter>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
